import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  X,
  Video,
  Calendar,
  User,
  ChevronRight,
  ArrowLeft,
  Search,
  FileText,
  Download,
  Loader2,
  Check,
  Filter,
} from 'lucide-react'
import * as api from '../services/api'
import type { TeamsMeeting } from '../types'

interface TeamsTranscript {
  id: string
  meeting_id: string
  created_at?: string
}

interface TeamsImportModalProps {
  projectId: string
  isOpen: boolean
  onClose: () => void
  onImportSuccess: () => void
}

export default function TeamsImportModal({
  projectId,
  isOpen,
  onClose,
  onImportSuccess,
}: TeamsImportModalProps) {
  // State
  const [meetings, setMeetings] = useState<TeamsMeeting[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [daysBack, setDaysBack] = useState<30 | 60 | 90>(30)
  const [searchTerm, setSearchTerm] = useState('')
  const [showOnlyWithTranscripts, setShowOnlyWithTranscripts] = useState(false)

  // Selected meeting and transcripts
  const [selectedMeeting, setSelectedMeeting] = useState<TeamsMeeting | null>(null)
  const [transcripts, setTranscripts] = useState<TeamsTranscript[]>([])
  const [loadingTranscripts, setLoadingTranscripts] = useState(false)
  const [importing, setImporting] = useState(false)

  // Track transcript availability
  const [transcriptAvailability, setTranscriptAvailability] = useState<
    Record<string, boolean | 'loading'>
  >({})

  // Fetch meetings
  const fetchMeetings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - daysBack)

      const response = await api.getTeamsMeetings(startDate.toISOString(), undefined, 100)
      setMeetings(response.data.meetings || [])
    } catch (err) {
      console.error('Failed to fetch meetings:', err)
      setError('Failed to fetch Teams meetings. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [daysBack])

  // Fetch meetings when modal opens or date range changes
  useEffect(() => {
    if (isOpen) {
      fetchMeetings()
    }
  }, [isOpen, fetchMeetings])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedMeeting(null)
      setTranscripts([])
      setSearchTerm('')
      setError(null)
    }
  }, [isOpen])

  // Check transcript availability for a meeting
  const checkTranscriptAvailability = useCallback(
    async (meeting: TeamsMeeting) => {
      if (transcriptAvailability[meeting.id] !== undefined) return

      setTranscriptAvailability((prev) => ({ ...prev, [meeting.id]: 'loading' }))
      try {
        // We already have has_transcript from the meeting data
        setTranscriptAvailability((prev) => ({
          ...prev,
          [meeting.id]: meeting.has_transcript || false,
        }))
      } catch {
        setTranscriptAvailability((prev) => ({ ...prev, [meeting.id]: false }))
      }
    },
    [transcriptAvailability]
  )

  // Check availability for visible meetings
  useEffect(() => {
    if (meetings.length > 0) {
      meetings.slice(0, 10).forEach((meeting) => {
        if (transcriptAvailability[meeting.id] === undefined) {
          checkTranscriptAvailability(meeting)
        }
      })
    }
  }, [meetings, transcriptAvailability, checkTranscriptAvailability])

  // Filter meetings
  const filteredMeetings = useMemo(() => {
    let filtered = [...meetings]

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (m) =>
          m.subject?.toLowerCase().includes(search) || m.organizer?.toLowerCase().includes(search)
      )
    }

    // Transcript availability filter
    if (showOnlyWithTranscripts) {
      filtered = filtered.filter((m) => m.has_transcript)
    }

    return filtered
  }, [meetings, searchTerm, showOnlyWithTranscripts])

  // Handle meeting selection
  const handleSelectMeeting = async (meeting: TeamsMeeting) => {
    setSelectedMeeting(meeting)
    setLoadingTranscripts(true)
    setTranscripts([])

    try {
      // Get transcripts for this meeting using join_url
      const response = await api.getMeetingTranscripts(meeting.id, meeting.join_url || undefined)
      setTranscripts(response.data.transcripts || [])
    } catch (err) {
      console.error('Failed to fetch transcripts:', err)
      setTranscripts([])
    } finally {
      setLoadingTranscripts(false)
    }
  }

  // Handle import
  const handleImport = async (_transcript: TeamsTranscript) => {
    if (!selectedMeeting) return

    setImporting(true)
    try {
      await api.importTeamsTranscript(projectId, {
        meeting_id: selectedMeeting.id,
        join_url: selectedMeeting.join_url,
      })
      onImportSuccess()
      onClose()
    } catch (err) {
      console.error('Failed to import transcript:', err)
      setError('Failed to import transcript. Please try again.')
    } finally {
      setImporting(false)
    }
  }

  // Format date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown'
    return new Date(dateStr).toLocaleDateString()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-slate-900 border border-slate-700/50 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <Video className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-100">
                  {selectedMeeting ? 'Select Transcript' : 'Import from Teams'}
                </h2>
                <p className="text-sm text-slate-400">
                  {selectedMeeting
                    ? `Transcripts for "${selectedMeeting.subject}"`
                    : 'Select a meeting to import its transcript'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Filters - only show in meetings list view */}
          {!selectedMeeting && (
            <div className="space-y-3">
              {/* Date range chips */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-500">Date range:</span>
                {([30, 60, 90] as const).map((days) => (
                  <button
                    key={days}
                    onClick={() => setDaysBack(days)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                      daysBack === days
                        ? 'bg-violet-500/20 border border-violet-500/30 text-violet-300'
                        : 'bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-slate-300'
                    }`}
                  >
                    Last {days} days
                  </button>
                ))}
              </div>

              {/* Search and filter */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search meetings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10 w-full"
                  />
                </div>
                <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showOnlyWithTranscripts}
                    onChange={(e) => setShowOnlyWithTranscripts(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-violet-500"
                  />
                  <span className="text-xs text-slate-400">With transcripts only</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="alert-error mb-4">
              <span>{error}</span>
            </div>
          )}

          {selectedMeeting ? (
            // Transcript selection view
            <div>
              <button
                onClick={() => {
                  setSelectedMeeting(null)
                  setTranscripts([])
                }}
                className="text-sm flex items-center gap-1 text-violet-400 hover:text-violet-300 mb-4 transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to meetings
              </button>

              {loadingTranscripts ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-2" />
                  <p className="text-slate-400">Loading transcripts...</p>
                </div>
              ) : transcripts.length > 0 ? (
                <div className="space-y-3">
                  {transcripts.map((transcript) => (
                    <div
                      key={transcript.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-200">Meeting Transcript</p>
                          <p className="text-sm text-slate-500">
                            {transcript.created_at
                              ? new Date(transcript.created_at).toLocaleString()
                              : 'Date unknown'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleImport(transcript)}
                        disabled={importing}
                        className="btn-primary btn-sm"
                      >
                        {importing ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Import
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-200 mb-2">No transcripts available</h3>
                  <p className="text-sm text-slate-500">
                    This meeting doesn't have any transcripts yet.
                    <br />
                    Transcripts are generated when recording is enabled.
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Meetings list view
            <div>
              {loading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-2" />
                  <p className="text-slate-400">Loading your Teams meetings...</p>
                </div>
              ) : filteredMeetings.length > 0 ? (
                <div className="space-y-3">
                  <div className="text-xs text-slate-500 mb-2">
                    Showing {filteredMeetings.length} of {meetings.length} meetings
                  </div>
                  {filteredMeetings.map((meeting) => {
                    const hasTranscript = meeting.has_transcript

                    return (
                      <div
                        key={meeting.id}
                        onClick={() => handleSelectMeeting(meeting)}
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800 hover:border-violet-500/30 cursor-pointer transition group"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                            <Video className="w-5 h-5 text-violet-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-200 truncate">
                              {meeting.subject || 'Untitled Meeting'}
                            </p>
                            <div className="flex items-center gap-3 text-sm text-slate-500 flex-wrap">
                              {meeting.start && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3.5 h-3.5" />
                                  {formatDate(meeting.start)}
                                </span>
                              )}
                              {meeting.organizer && (
                                <span className="flex items-center gap-1 truncate max-w-[150px]">
                                  <User className="w-3.5 h-3.5" />
                                  {meeting.organizer}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {hasTranscript ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Has transcript
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-700/50 text-slate-500 border border-slate-600/30">
                              No transcript
                            </span>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-violet-400 transition" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : meetings.length > 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                    <Filter className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-200 mb-2">No matching meetings</h3>
                  <p className="text-sm text-slate-500">Try adjusting your search or filter.</p>
                  <button
                    onClick={() => {
                      setSearchTerm('')
                      setShowOnlyWithTranscripts(false)
                    }}
                    className="mt-4 btn-secondary btn-sm"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                    <Video className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-200 mb-2">No meetings found</h3>
                  <p className="text-sm text-slate-500">
                    No Teams meetings found in the selected date range.
                    <br />
                    Try selecting a longer date range.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
